import { LocalAtRecord } from '../../db';
import { JobsFieldSet } from '@rogoag/airtable/crm';

interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

/**
 * Domain entity representing a job in the Skips Processing workflow
 */
export class SkipsProcessingJob {
  public readonly id: string;
  public readonly name: string;
  public readonly jobRecord: LocalAtRecord<JobsFieldSet>;
  public readonly operator: string;
  public readonly sampleDate: string;

  // Read-only display fields
  public readonly rogoJobId: string; // This is also used as the job ID
  public readonly invoiceNumber: string[]; // Link to Invoice records
  public readonly archiveJobLink: string[];
  public readonly contJobLink: string[];
  public readonly deletedSamplesInExe: string;
  public readonly skipsInExe: string;
  public readonly jobFlags: string[]; // Job Flags is an array in Airtable
  public readonly dataSent: string[]; // Data Sent is an array in Airtable

  // Editable fields (local state)
  private _shift: string[]; // Shift is a link field (array of shift record IDs)
  private _shiftName: string; // Cache for shift name (fetched from Shifts table)
  private _billablePercent: number; // Stored as 0-100 for UI
  private _numSamples: number;
  private _exeNumSamples: number;
  private _exeNumSamplesSkipped: number;
  private _previouslySampledExeNum: number;
  private _mgrNotesOps: string;

  // Track original values for change detection
  private readonly _originalValues: {
    shift: string[];
    billablePercent: number;
    numSamples: number;
    exeNumSamples: number;
    exeNumSamplesSkipped: number;
    previouslySampledExeNum: number;
    mgrNotesOps: string;
    invoiceNumber: number; // Store length of invoice array
  };

  constructor(jobRecord: LocalAtRecord<JobsFieldSet>) {
    this.id = jobRecord.id;
    this.name = jobRecord.get('Name') || jobRecord.id;
    this.jobRecord = jobRecord;

    // Operator Name is an array, get first element
    const operatorName = jobRecord.get('Operator Name');
    this.operator = Array.isArray(operatorName) && operatorName.length > 0 ? operatorName[0] : '';

    this.sampleDate = jobRecord.get('Sample Date') || '';

    // Read-only fields
    this.rogoJobId = jobRecord.get('Rogo Job ID') || '';
    this.invoiceNumber = this._getArrayField('Invoice #');
    this.archiveJobLink = this._getArrayField('Archive Job Link Skip Mgmt');
    this.contJobLink = this._getArrayField('Cont Job Link Skip Mgmt');
    this.deletedSamplesInExe = jobRecord.get('Deleted Sples in Exe? (Skip Mgmt)') || '';
    this.skipsInExe = jobRecord.get('Skips in Exe? (Skip Mgmt)') || '';
    this.jobFlags = this._getArrayField('Job Flags');
    this.dataSent = this._getArrayField('Data Sent');

    // Editable fields - convert from 0-1 to 0-100 for billable percent
    this._shift = this._getArrayField('Shift'); // Right now, we use it as read-only
    this._shiftName = ''; // Will be loaded separately if needed
    const billablePercentRaw = jobRecord.get('Billable Percent %');
    this._billablePercent = typeof billablePercentRaw === 'number' ? billablePercentRaw * 100 : 0;
    this._numSamples = jobRecord.get('# Samples') || 0;
    this._exeNumSamples = jobRecord.get('Exe # Samples') || 0;
    this._exeNumSamplesSkipped = jobRecord.get('Exe # Samples Skipped') || 0;
    this._previouslySampledExeNum = jobRecord.get('Previously Sampled Exe # - Skip Mgmt') || 0;
    this._mgrNotesOps = jobRecord.get('Mgr Notes #ops') || '';

    // Store original values
    this._originalValues = {
      shift: this._shift,
      billablePercent: this._billablePercent,
      numSamples: this._numSamples,
      exeNumSamples: this._exeNumSamples,
      exeNumSamplesSkipped: this._exeNumSamplesSkipped,
      previouslySampledExeNum: this._previouslySampledExeNum,
      mgrNotesOps: this._mgrNotesOps,
      invoiceNumber: this.invoiceNumber.length,
    };
  }

  private _getArrayField(fieldName: keyof JobsFieldSet): string[] {
    const value = this.jobRecord.get(fieldName);

    if (Array.isArray(value)) {
      return value;
    }

    return [];
  }

  // Job type determination
  get isArchivedJob(): boolean {
    return this.contJobLink.length > 0;
  }

  get isContinuationJob(): boolean {
    return this.archiveJobLink.length > 0;
  }

  // Getters for editable fields
  get shift(): string[] {
    return this._shift;
  }

  // Helper to get shift name for display (fetches from first linked shift)
  get shiftName(): string {
    return this._shiftName || (this._shift.length > 0 ? 'Loading...' : '');
  }

  setShiftName(name: string): void {
    this._shiftName = name;
  }

  get hasShift(): boolean {
    return this._shift.length > 0;
  }

  get billablePercent(): number {
    return this._billablePercent;
  }

  get numSamples(): number {
    return this._numSamples;
  }

  get exeNumSamples(): number {
    return this._exeNumSamples;
  }

  get exeNumSamplesSkipped(): number {
    return this._exeNumSamplesSkipped;
  }

  get previouslySampledExeNum(): number {
    return this._previouslySampledExeNum;
  }

  get mgrNotesOps(): string {
    return this._mgrNotesOps;
  }

  // Calculated field: Pulled Percent %
  get pulledPercent(): number {
    if (this.isArchivedJob) {
      if (this._exeNumSamples === 0) {
        return 0;
      }

      return (
        ((this._exeNumSamples - this._exeNumSamplesSkipped - this._previouslySampledExeNum) / this._exeNumSamples) * 100
      );
    } else {
      // Continuation job
      if (this._numSamples === 0) {
        return 0;
      }

      return ((this._numSamples - this._exeNumSamplesSkipped - this._previouslySampledExeNum) / this._numSamples) * 100;
    }
  }

  // Setters for editable fields
  setShift(shift: string[]): void {
    this._shift = shift;
  }

  setBillablePercent(percent: number): void {
    this._billablePercent = percent;
  }

  setNumSamples(count: number): void {
    this._numSamples = count;
  }

  setExeNumSamples(count: number): void {
    this._exeNumSamples = count;
  }

  setExeNumSamplesSkipped(count: number): void {
    this._exeNumSamplesSkipped = count;
  }

  setPreviouslySampledExeNum(count: number): void {
    this._previouslySampledExeNum = count;
  }

  setMgrNotesOps(notes: string): void {
    this._mgrNotesOps = notes;
  }

  // Validation
  validate(): ValidationResult {
    const errors: string[] = [];

    if (this.isArchivedJob) {
      // Archived job rules
      if (this._billablePercent !== 0) {
        errors.push('billablePercent');
      }

      if (this.invoiceNumber.length > 0) {
        errors.push('invoiceNumber');
      }

      if (this._shift.length === 0) {
        errors.push('shift');
      }
    } else if (this.isContinuationJob) {
      // Continuation job rules
      if (this._billablePercent !== 100) {
        errors.push('billablePercent');
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  // Change tracking
  hasChanges(): boolean {
    return (
      JSON.stringify(this._shift) !== JSON.stringify(this._originalValues.shift) ||
      this._billablePercent !== this._originalValues.billablePercent ||
      this._numSamples !== this._originalValues.numSamples ||
      this._exeNumSamples !== this._originalValues.exeNumSamples ||
      this._exeNumSamplesSkipped !== this._originalValues.exeNumSamplesSkipped ||
      this._previouslySampledExeNum !== this._originalValues.previouslySampledExeNum ||
      this._mgrNotesOps !== this._originalValues.mgrNotesOps
    );
  }

  resetChanges(): void {
    this._shift = this._originalValues.shift;
    this._billablePercent = this._originalValues.billablePercent;
    this._numSamples = this._originalValues.numSamples;
    this._exeNumSamples = this._originalValues.exeNumSamples;
    this._exeNumSamplesSkipped = this._originalValues.exeNumSamplesSkipped;
    this._previouslySampledExeNum = this._originalValues.previouslySampledExeNum;
    this._mgrNotesOps = this._originalValues.mgrNotesOps;
  }

  getChangedFields(): Partial<Record<keyof JobsFieldSet, any>> {
    const changes: Partial<Record<keyof JobsFieldSet, any>> = {};

    if (JSON.stringify(this._shift) !== JSON.stringify(this._originalValues.shift)) {
      changes['Shift'] = this._shift;
    }

    if (this._billablePercent !== this._originalValues.billablePercent) {
      // Convert back to 0-1 for Airtable
      changes['Billable Percent %'] = this._billablePercent / 100;
    }

    if (this._numSamples !== this._originalValues.numSamples) {
      changes['# Samples'] = this._numSamples;
    }

    if (this._exeNumSamples !== this._originalValues.exeNumSamples) {
      changes['Exe # Samples'] = this._exeNumSamples;
    }

    if (this._exeNumSamplesSkipped !== this._originalValues.exeNumSamplesSkipped) {
      changes['Exe # Samples Skipped'] = this._exeNumSamplesSkipped;
    }

    if (this._previouslySampledExeNum !== this._originalValues.previouslySampledExeNum) {
      changes['Previously Sampled Exe # - Skip Mgmt'] = this._previouslySampledExeNum;
    }

    if (this._mgrNotesOps !== this._originalValues.mgrNotesOps) {
      changes['Mgr Notes #ops'] = this._mgrNotesOps;
    }

    return changes;
  }
}
